import * as R from 'ramda';
import {api} from './services/api';
import {renameKey} from '../../common/helpers/rename-key.helper';
import {spreadProp} from '../../common/helpers/spread-prop.helper';
import {getDefaultConfigurationOf} from '../data/default-configurations';

import hoseOrientations from '../data/hose-orientations.json';
import instrumentCounts from '../data/instrumentCounts.json';
import instrumentsGroups from '../data/instrument-groups.json';

const mapByName = a => a.reduce((acc, val) => Object.assign(acc, {[val.name]: val}), {});

class AppData {
	constructor() {
		this.instrumentsGroups = R.mapObjIndexed(mapByName, instrumentsGroups);
		this.hoseOrientations = hoseOrientations;
		this.instrumentCounts = instrumentCounts;
	}

	async fetch() {
		const {result} = (await api.get('/getElements')).data;

		this.unitNames = R.pipe(R.filter(R.propEq('type', 'units')), R.pluck('name'))(result);
		this.typesToElements = R.pipe(
			// Monitor 'philips-24' is temporarily disabled.
			R.filter(R.complement(R.propEq('name', 'philips-24'))),
			R.map(R.pipe(spreadProp('metadata'), renameKey('full_name', 'fullName'), R.dissoc('id'))),
			R.groupBy(R.prop('type')),
			R.mapObjIndexed(R.groupBy(R.prop('unit_name')))
		)(result);

		this.unitsList = this.typesToElements.units['I am a unit'];
		this.units = mapByName(this.typesToElements.units['I am a unit']);
		this.colorsList = this.getList('colors');
		this.colors = this.getMapByName('colors');
		this.chairsList = this.getList('chairs');
		this.chairs = this.getMapByName('chairs');
		this.chairAccessoriesList = this.getList('chair-accessories');
		this.chairAccessories = this.getMapByName('chair-accessories');
		this.physicianChairs = this.getMapByName('physician-chairs');
		this.instrumentsList = this.getList('instruments');
		this.instruments = this.getMapByName('instruments');
		this.instrumentTips = this.getMapByName('instrument-tips');
		this.footControlsList = this.getList('foot-controls');
		this.footControls = this.getMapByName('foot-controls');
		this.lamps = this.getMapByName('lamps');
		this.lampOptions = this.getMapByName('lamp-options');
		this.monitors = this.getMapByName('monitors');
		this.monitorOptions = this.getMapByName('monitor-options');
		this.lampMastAccessories = this.getMapByName('lamp-mast-accessories');
		this.lampMastAccessoriesAdditionalOptions = this.getMapByName(
			'lamp-mast-accessories-additional-options'
		);
		this.suctionSystemsList = this.getList('suction-systems');
		this.suctionSystems = this.getMapByName('suction-systems');
		this.waterSuppliesList = this.getList('water-supplies');
		this.waterSupplies = this.getMapByName('water-supplies');
		this.extraColorOptionsList = this.getList('extra-color-options');
		this.extraColorOptions = this.getMapByName('extra-color-options');
		this.addonsList = this.getList('addons');
		this.addons = this.getMapByName('addons');
		this.physicianTableAccessoriesList = this.getList('physician-table-accessories');
		this.physicianTableAccessories = this.getMapByName('physician-table-accessories');
		this.assistantTableAccessoriesList = this.getList('assistant-table-accessories');
		this.assistantTableAccessories = this.getMapByName('assistant-table-accessories');
		this.hoseDeliverySystemsList = this.getList('hose-delivery-systems');
		this.hoseDeliverySystems = this.getMapByName('hose-delivery-systems');
	}

	getList(type) {
		const preresult = R.merge(
			R.zipObj(this.unitNames, R.map(R.always([]), this.unitNames)),
			this.typesToElements[type]
		);
		if (type === 'monitors') {
			return R.mergeDeepWith(
				R.concat,
				R.zipObj(
					this.unitNames,
					R.map(
						R.always([
							{
								name: null,
								fullName: 'Brak',
								cost: 0,
								image: null,
							},
						]),
						R.without(['smile-mini-04'], this.unitNames)
					)
				),
				preresult
			);
		}
		if (type === 'lamp-mast-accessories') {
			return R.mergeDeepWith(
				R.concat,
				R.zipObj(
					this.unitNames,
					R.map(
						R.always([
							{
								name: 'lma-blank',
								fullName: 'Brak',
								cost: 0,
							},
						]),
						R.without(['smile-mini-04'], this.unitNames)
					)
				),
				preresult
			);
		}

		return preresult;
	}

	getMapByName(type) {
		return R.mapObjIndexed(mapByName, this.getList(type));
	}

	getDefaultConfigurationData(unitName) {
		return R.mergeAll([
			{unit: unitName},
			getDefaultConfigurationOf('_'),
			getDefaultConfigurationOf(unitName),
		]);
	}

	getDefaultConfigurationProp(unitName, prop) {
		return this.getDefaultConfigurationData(unitName)[prop];
	}

	getChairAccessories(unitName, chairName) {
		return this.chairAccessoriesList[unitName].filter(accessory =>
			accessory.name.startsWith(chairName)
		);
	}
}

export default new AppData();
